// @flow
import SearchUrlFormatter from './formatters/SearchUrlFormatter';
import type {SearchRequest} from './models/SearchRequest';
import petSyncer from './utils/petSyncer';
import type {SyncWithStrategy} from './utils/petSyncer';
import {makeMinValueEnforcer} from './utils/enforcer';
import {ADULTS_COUNT, CHILDREN_COUNT} from './models/SearchUrlParameters';

export const ROOT_PATH = '/search';

export type Root = typeof ROOT_PATH;

type Options = {
    petSyncStrategy?: SyncWithStrategy,
    canonicalizeKeywords?: boolean,
    siteContext?: Object
};

/**
 * Generates a /search style search page URL for the given {@link SearchRequest}. By default this function generates
 * SEO compliant relative /search URLs with keyword canonicalization that lowercases the destination keywords, replaces whitespace with dashes, and strips out non-alphanumeric characters
 *
 * @example
 * toURL({
 *  q: 'Austin, TX, USA',
 *  filter: ['filter:38'],
 *  petInclued: true
 * });
 * // returns
 * // '/search/keywords:austin-tx-usa/filter:38?petInclued=true'
 *
 * toURL({
 *  q: 'Austin, TX, USA',
 *  filter: ['filter:38'],
 *  petInclued: true
 * }, {
 *  canonicalizeKeywords: false
 * });
 * // returns
 * // '/search/keywords:Austin%2C%20TX%2C%20USA/filter:38?petInclued=true)
 * @param searchRequest - Search Request model
 * @param {object} [options] - toURL options
 * @param {SyncWithStrategy} [options.petSyncStrategy] - Strategy to use for syncing the pet refinements with petIncluded trip data, defaults to 'both'
 * @param {boolean} [options.canonicalizeKeywords] - Turns off canonical keyword transform and use URI encoding instead
 * @returns {string} with the search page relative URL for the given {@link SearchRequest} object.
 */
const pickParams = (searchRequest: SearchRequest): SearchRequest => {
    const enforceMinValue = makeMinValueEnforcer(searchRequest);
    const {adultsCount, childrenCount} = {
        ...enforceMinValue(ADULTS_COUNT, 0),
        ...enforceMinValue(CHILDREN_COUNT, -1)
    };
    let filteredSearchRequest = {...searchRequest};

    if (!adultsCount) {
        delete filteredSearchRequest[ADULTS_COUNT];
    }

    // Allow childrenCount to be 0
    if (!childrenCount && childrenCount !== 0) {
        delete filteredSearchRequest[CHILDREN_COUNT];
    }

    return filteredSearchRequest;
};

export default function toURL(
    searchRequest: SearchRequest,
    options: Options = {}
): string {
    // sync pets
    const withSyncedPets = petSyncer(searchRequest, options.petSyncStrategy);
    const sanitizedParams = pickParams(withSyncedPets);
    const sanitizedSearchRequest = {...sanitizedParams};
    const {canonicalizeKeywords} = {canonicalizeKeywords: true, ...options};

    const formattedSearchUrl: string = new SearchUrlFormatter().format(
        sanitizedSearchRequest,
        {canonicalizeKeywords}
    );

    return formattedSearchUrl;
}
